// Subtask quick-peek drawer. Click a 子项 in the overview tree (or the TaskDetail
// 子项 table) to read AND edit its detail without leaving the page: rich
// description + comments. Editable when updateTask / addUpdate are provided;
// otherwise read-only with a "打开完整待办" link. Closes on Esc / overlay click.
// (See plan-design-review: side panel chosen over inline expand.)

import { useEffect, useState } from 'react';
import { RichContent, RichDescriptionEditor } from './RichDescription.jsx';
import { RichTextEditor } from './RichTextEditor.jsx';
import { normalizeRichHtml } from '../lib/sanitize.ts';
import { Avatar } from './Avatar.jsx';
import { Prio, StatusPill } from './atoms.jsx';
import { Icon } from './Icon.jsx';
import { fmtDateTime, fmtShort, isOverdue } from '../lib/dates.ts';

const SUBTASK_STATUSES = ['待开始', '进行中', '阻塞', '已完成', '已取消'];

export const SubtaskPanel = ({ task, staff = [], onClose, onOpenFull, updateTask, addUpdate }) => {
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!task) return null;
  const who = staff.find(s => s.id === task.assigneeId);
  const over = isOverdue(task);
  const updates = [...(task.updates || [])].reverse();
  const whoName = id => staff.find(s => s.id === id)?.name || id || '—';
  const canEditDesc = typeof updateTask === 'function';
  const canComment = typeof addUpdate === 'function';

  async function send() {
    const next = normalizeRichHtml(draft);
    if (!next || sending) return;
    setSending(true);
    try { await addUpdate(task.id, next); setDraft(''); }
    catch { /* app-level handler surfaces the toast */ }
    finally { setSending(false); }
  }

  return (
    <div className="sp-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <aside className="sp-drawer" onClick={e => e.stopPropagation()}>
        <div className="sp-head">
          <div className="sp-head-main">
            <Prio value={task.priority} />
            <h2 className="sp-title">{task.title}</h2>
          </div>
          <button className="sp-close" onClick={onClose} aria-label="关闭"><Icon name="x" /></button>
        </div>

        <div className="sp-meta">
          <span className="sp-owner"><Avatar staff={who} /> {who?.name || '—'}{who?.title ? ` · ${who.title}` : ''}</span>
          {/* Inline-editable status + 截止时间, auto-saved via updateTask (optimistic
              + REST; the real-time sync propagates). Discrete controls → no debounce. */}
          {canEditDesc ? (
            <>
              <select className="sp-edit sp-edit-status" value={task.status}
                      onChange={e => updateTask(task.id, { status: e.target.value })}>
                {SUBTASK_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <label className={`sp-edit sp-edit-due${over ? ' is-over' : ''}`}>截止
                <input type="date" value={(task.dueDate || '').slice(0, 10)}
                       onChange={e => updateTask(task.id, { dueDate: e.target.value || null })} />
              </label>
            </>
          ) : (
            <>
              <StatusPill value={task.status} />
              {task.dueDate && (
                <span className={`sp-due mono${over ? ' is-over' : ''}`}>截止 {fmtShort(task.dueDate)}{over ? ' · 逾期' : ''}</span>
              )}
            </>
          )}
        </div>

        <div className="sp-body">
          <section className="sp-section">
            <h3 className="sp-section-title">待办详情</h3>
            {canEditDesc ? (
              <RichDescriptionEditor
                value={task.detail || task.description || ''}
                onSave={(v) => updateTask(task.id, { detail: v ?? '' })}
                taskId={task.id}
                staff={staff}
                placeholder="（点击添加描述 · 支持 @同事 和 粘贴图片）" />
            ) : (task.detail || task.description
              ? <RichContent value={task.detail || task.description} staff={staff} />
              : <div className="sp-empty">没有描述</div>)}
          </section>

          <section className="sp-section">
            <h3 className="sp-section-title">评论 & 进度{updates.length ? ` · ${updates.length}` : ''}</h3>
            {canComment && (
              <div className="sp-compose">
                <RichTextEditor
                  value={draft} onChange={setDraft} staff={staff} taskId={task.id}
                  compact rows={2}
                  placeholder="评论、进度更新、@同事 · ⌘+Enter 发送"
                  onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); void send(); } }} />
                <button type="button" className="btn btn-primary btn-sm" onClick={send}
                        disabled={sending || !normalizeRichHtml(draft)}>
                  {sending ? '发送中…' : '发送'}
                </button>
              </div>
            )}
            {updates.length === 0 ? (
              !canComment && <div className="sp-empty">还没有评论</div>
            ) : (
              <div className="sp-updates">
                {updates.map((u, i) => (
                  <div className="sp-update" key={i}>
                    <div className="sp-update-meta mono">{fmtDateTime(u.at)} · {whoName(u.by)}</div>
                    <RichContent value={u.text} staff={staff} className="sp-update-text" />
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        <div className="sp-foot">
          <button type="button" className="btn btn-primary btn-sm"
                  onClick={() => { onOpenFull(task.id); onClose(); }}>
            打开完整待办 <Icon name="arrow" />
          </button>
        </div>
      </aside>
    </div>
  );
};
